import { useContext, useRef, useState } from "react";
import clsx from "clsx";
import { useNavigate, useParams, useSearchParams } from "@remix-run/react";

import TaxContext from "~/contexts/TaxContext";
import OrderContext from "~/contexts/OrderContext";
import ThemeContext from "~/contexts/ThemeContext";
import Button from "~/components/Button/Button";
import { useOutsideClick } from "~/hooks/useOutsideClick";
import { getCartItemCount } from "~/helpers/cartHelpers";

import styles from "./CartDropdown.module.css";
import { urlGen } from "~/helpers/routesHelpers";
import SiteContext from "~/contexts/SiteContext";
import Cart from "../Cart/Cart";
import { NavBarSize } from "~/theme/models/themeModels";
import { getLuma } from "~/helpers/colorHelpers";
import CartIcon from "~/icons/CartIcon";

const CartDropdown = () => {
  const [visibleDropdown, setVisibleDropdown] = useState(false);

  const { orderSummary, orderSource } = useContext(OrderContext);
  const { formatAmount } = useContext(TaxContext);
  const { template } = useContext(ThemeContext);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const { site } = useContext(SiteContext);
  const [searchParams] = useSearchParams();
  const menuCode = searchParams.get("menuCode");
  const params = useParams();

  useOutsideClick([triggerRef, dropdownRef], () => setVisibleDropdown(false));

  const handleCheckoutClick = () => {
    setVisibleDropdown(false);
    navigate(
      urlGen("Checkout", {
        orderSource,
        siteId: site?.id,
        menuCode,
        tableNumber: params.table,
      }),
    );
  };

  // Styles
  const backgroundColor = template.cart.backgroundColour;

  const showAdditionalProducts = template.cart.showAdditionalProducts;

  const cartTriggerFontColor =
    getLuma(template.navBar.backgroundColour) < 150 ? "#fff" : "#000";
  const cartTriggerIndicatorFontColor = template.navBar.backgroundColour;
  const cartTriggerHeight = NavBarSize[template.navBar.size];
  const cartItemCount = getCartItemCount(orderSummary.lines);

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        ref={triggerRef}
        className={styles.trigger}
        onClick={() => setVisibleDropdown(!visibleDropdown)}
        style={{ lineHeight: cartTriggerHeight, color: cartTriggerFontColor }}
        aria-label={`View cart — ${cartItemCount} ${
          cartItemCount === 1 ? "item" : "items"
        }, ${formatAmount(orderSummary?.finalPriceIncTax ?? 0)}`}
        aria-expanded={visibleDropdown}
      >
        {/* A bag icon makes it obvious this is the cart (not just a price). */}
        <CartIcon color={cartTriggerFontColor} />
        <span>{formatAmount(orderSummary?.finalPriceIncTax ?? 0)}</span>
        {cartItemCount > 0 && (
          <div
            className={styles.countIndicator}
            style={{
              color: cartTriggerIndicatorFontColor,
              backgroundColor: cartTriggerFontColor,
            }}
          >
            {cartItemCount}
          </div>
        )}
      </button>
      <div
        ref={dropdownRef}
        className={clsx(
          styles.dropdown,
          !visibleDropdown && styles.dropdownHidden,
        )}
        style={{
          backgroundColor,
        }}
      >
        <div className={styles.cartContent}>
          {!!orderSummary.lines.length && (
            <div className={styles.buttonWrapper}>
              <Button onClick={handleCheckoutClick}>
                <span>
                  Go to checkout{" "}
                  <b>{formatAmount(orderSummary?.finalPriceIncTax ?? 0)}</b>
                </span>
              </Button>
            </div>
          )}
          <Cart
            showAdditionalProducts={showAdditionalProducts}
            readOnly={false}
            clearable
            hideAmountsForMobile={false}
          />
        </div>
      </div>
    </div>
  );
};

export default CartDropdown;
